<template>
  <div class="inventory-report-shell">
    <!-- HERO -->
    <section class="inventory-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Inventory Value</h1>
        <p class="hero-copy">
          See what is sitting in the cellar, what it is worth, and which bottles deserve attention.
        </p>
      </div>

      <button @click="fetchReport" class="primary-action">
        Generate Report
      </button>
    </section>

    <!-- SUMMARY CARDS -->
    <section class="summary-grid">
      <article class="summary-card">
        <span>Total Value</span>
        <strong>${{ money(totalInventoryValue) }}</strong>
      </article>

      <article class="summary-card">
        <span>Total Units</span>
        <strong>{{ number(totalUnits) }}</strong>
      </article>

      <article class="summary-card">
        <span>Item Count</span>
        <strong>{{ filteredReportData.length }}</strong>
      </article>

      <article class="summary-card">
        <span>Avg Unit Cost</span>
        <strong>${{ money(averageUnitCost) }}</strong>
      </article>
    </section>

    <!-- FILTERS -->
    <section class="inventory-toolbar">
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input
          type="text"
          v-model="searchText"
          placeholder="Search item, region, type, class..."
        />
      </div>

      <label>
        <span>Category</span>
        <select v-model="selectedCategory">
          <option value="">All Categories</option>
          <option
            v-for="category in categories"
            :key="category"
            :value="category"
          >
            {{ category }}
          </option>
        </select>
      </label>

      <label>
        <span>Class</span>
        <select v-model="selectedClass">
          <option value="">All Classes</option>
          <option value="pour">Pour</option>
          <option value="reserve">Reserve</option>
          <option value="bottle">Bottle</option>
          <option value="plate">Plate</option>
          <option value="supply">Supply</option>
          <option value="merch">Merch</option>
          <option value="other">Other</option>
        </select>
      </label>

      <label>
        <span>Sort</span>
        <select v-model="sortBy">
          <option value="value_desc">Value: High to Low</option>
          <option value="value_asc">Value: Low to High</option>
          <option value="name_asc">Name: A to Z</option>
          <option value="qty_desc">Quantity: High to Low</option>
          <option value="cost_desc">Unit Cost: High to Low</option>
        </select>
      </label>

      <button type="button" @click="clearFilters" class="ghost-action">
        Clear
      </button>
    </section>

    <!-- INVENTORY CARDS -->
    <section v-if="filteredReportData.length" class="inventory-grid">
      <article
        v-for="item in sortedReportData"
        :key="item.item_no"
        class="inventory-card"
        @click="openItemDetails(item)"
      >
        <div class="card-top">
          <div>
            <p class="label">Item #{{ item.item_no }}</p>
            <h2>{{ item.item_name || 'Unnamed Item' }}</h2>
          </div>

          <span class="value-pill">
            ${{ money(item.total_inventory_value) }}
          </span>
        </div>

        <div class="meta-row">
          <span>{{ item.category_name || 'No Category' }}</span>
          <span>{{ item.wine_type || 'Item' }}</span>
          <span>{{ formatClass(item.service_class) }}</span>
        </div>

        <div class="inventory-metrics">
          <div>
            <span>Qty</span>
            <strong>{{ number(item.quantity_on_hand) }}</strong>
          </div>

          <div>
            <span>MAC</span>
            <strong>${{ money(item.moving_avg_cost) }}</strong>
          </div>

          <div>
            <span>Value</span>
            <strong>${{ money(item.total_inventory_value) }}</strong>
          </div>
        </div>

        <div class="card-footer">
          <span
            class="stock-badge"
            :class="{ low: Number(item.quantity_on_hand || 0) <= 5 }"
          >
            {{ Number(item.quantity_on_hand || 0) <= 5 ? 'Low Stock' : 'In Stock' }}
          </span>

          <span class="details-link">Details →</span>
        </div>
      </article>
    </section>

    <!-- EMPTY STATE -->
    <section v-else class="empty-state">
      <p class="eyebrow">No Inventory</p>
      <h2>No matching inventory found.</h2>
      <p>Generate the report or adjust your filters.</p>
    </section>

    <!-- DETAIL POPUP -->
    <div v-if="showDetailPopup" class="modal-overlay">
      <div class="inventory-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Inventory Detail</p>
            <h2>{{ selectedItem.item_name }}</h2>
            <p>
              Item #{{ selectedItem.item_no }} · {{ selectedItem.category_name || 'No Category' }}
            </p>
          </div>

          <button type="button" @click="showDetailPopup = false" class="icon-close">
            ×
          </button>
        </header>

        <div class="modal-body">
          <section class="detail-hero">
            <div>
              <span>Total Inventory Value</span>
              <strong>${{ money(selectedItem.total_inventory_value) }}</strong>
            </div>

            <div>
              <span>Quantity On Hand</span>
              <strong>{{ number(selectedItem.quantity_on_hand) }}</strong>
            </div>

            <div>
              <span>Moving Avg Cost</span>
              <strong>${{ money(selectedItem.moving_avg_cost) }}</strong>
            </div>
          </section>

          <section class="detail-grid">
            <div>
              <span>Wine Type</span>
              <strong>{{ selectedItem.wine_type || '—' }}</strong>
            </div>

            <div>
              <span>Class</span>
              <strong>{{ formatClass(selectedItem.service_class) }}</strong>
            </div>

            <div>
              <span>Vintage</span>
              <strong>{{ selectedItem.vintage || '—' }}</strong>
            </div>

            <div>
              <span>Bottle Size</span>
              <strong>{{ selectedItem.bottle_size || '—' }}</strong>
            </div>

            <div>
              <span>Producer / Chateau</span>
              <strong>{{ selectedItem.chateau || '—' }}</strong>
            </div>

            <div>
              <span>Region</span>
              <strong>{{ selectedItem.region || '—' }}</strong>
            </div>

            <div>
              <span>Country</span>
              <strong>{{ selectedItem.country || '—' }}</strong>
            </div>

            <div>
              <span>Last Sale</span>
              <strong>{{ formatDate(selectedItem.last_sale_date) }}</strong>
            </div>
          </section>

          <section v-if="selectedItem.description || selectedItem.details" class="detail-notes">
            <span>Notes</span>
            <p>{{ selectedItem.description || selectedItem.details }}</p>
          </section>

          <footer class="modal-actions">
            <button type="button" @click="showDetailPopup = false" class="primary-action">
              Close
            </button>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'InventoryValueReport',

  data() {
    return {
      reportData: [],
      categories: [],

      searchText: '',
      selectedCategory: '',
      selectedClass: '',
      sortBy: 'value_desc',

      showDetailPopup: false,
      selectedItem: {}
    };
  },

  computed: {
    filteredReportData() {
      const search = String(this.searchText || '').toLowerCase();

      return this.reportData.filter(item => {
        const searchable = [
          item.item_no,
          item.item_name,
          item.category_name,
          item.wine_type,
          item.service_class,
          item.vintage,
          item.bottle_size,
          item.chateau,
          item.region,
          item.country,
          item.description,
          item.details
        ]
          .map(value => String(value || '').toLowerCase())
          .join(' ');

        const matchesSearch =
          !search || searchable.includes(search);

        const matchesCategory =
          !this.selectedCategory ||
          item.category_name === this.selectedCategory;

        const matchesClass =
          !this.selectedClass ||
          String(item.service_class || '').toLowerCase() === this.selectedClass;

        return matchesSearch && matchesCategory && matchesClass;
      });
    },

    sortedReportData() {
      const data = this.filteredReportData.slice();

      return data.sort((a, b) => {
        if (this.sortBy === 'value_asc') {
          return Number(a.total_inventory_value || 0) - Number(b.total_inventory_value || 0);
        }

        if (this.sortBy === 'name_asc') {
          return String(a.item_name || '').localeCompare(String(b.item_name || ''));
        }

        if (this.sortBy === 'qty_desc') {
          return Number(b.quantity_on_hand || 0) - Number(a.quantity_on_hand || 0);
        }

        if (this.sortBy === 'cost_desc') {
          return Number(b.moving_avg_cost || 0) - Number(a.moving_avg_cost || 0);
        }

        return Number(b.total_inventory_value || 0) - Number(a.total_inventory_value || 0);
      });
    },

    totalInventoryValue() {
      return this.filteredReportData.reduce((sum, item) => {
        return sum + Number(item.total_inventory_value || 0);
      }, 0);
    },

    totalUnits() {
      return this.filteredReportData.reduce((sum, item) => {
        return sum + Number(item.quantity_on_hand || 0);
      }, 0);
    },

    averageUnitCost() {
      if (!this.totalUnits) {
        return 0;
      }

      return this.totalInventoryValue / this.totalUnits;
    }
  },

  methods: {
    async fetchCategories() {
      try {
        const response = await api.get('/inventory');
        const uniqueCategories = [
          ...new Set((response.data || []).map(item => item.category_name).filter(Boolean))
        ];

        this.categories = uniqueCategories.sort((a, b) => a.localeCompare(b));
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    },

    async fetchReport() {
      try {
        const response = await api.get('/reports/inventory-value', {
          params: {
            filter_by: 'category',
            filter_value: ''
          }
        });

        this.reportData = (response.data || []).map(item => ({
          ...item,
          quantity_on_hand: Number(item.quantity_on_hand || 0),
          moving_avg_cost: Number(item.moving_avg_cost || 0),
          total_inventory_value: Number(item.total_inventory_value || 0)
        }));
      } catch (error) {
        console.error('Error fetching Inventory Value report:', error);
        alert('Failed to fetch Inventory Value report. Check backend logs.');
      }
    },

    openItemDetails(item) {
      this.selectedItem = item;
      this.showDetailPopup = true;
    },

    clearFilters() {
      this.searchText = '';
      this.selectedCategory = '';
      this.selectedClass = '';
      this.sortBy = 'value_desc';
    },

    money(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    number(value) {
      return Number(value || 0).toLocaleString('en-US', {
        maximumFractionDigits: 4
      });
    },

    formatClass(value) {
      if (!value) {
        return '—';
      }

      return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },

    formatDate(date) {
      if (!date) {
        return '—';
      }

      return new Date(date).toLocaleDateString('en-US');
    }
  },

  mounted() {
    this.fetchCategories();
    this.fetchReport();
  }
};
</script>

<style scoped>
.inventory-report-shell {
  min-height: 100vh;
  padding: 28px;
  color: #f7efe8;
  font-family: "Inter", "Poppins", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.38), transparent 34%),
    radial-gradient(circle at bottom right, rgba(194, 74, 47, 0.18), transparent 32%),
    linear-gradient(135deg, #0d090d 0%, #171016 42%, #24141b 100%);
}

.inventory-hero,
.inventory-toolbar,
.summary-card,
.inventory-card,
.empty-state,
.inventory-modal {
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.085), rgba(255, 255, 255, 0.035));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px);
}

.inventory-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 30px;
}

.eyebrow,
.label {
  margin: 0 0 8px;
  color: #c8a66a;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 12px;
  font-weight: 900;
}

.label {
  color: #9b8f8b;
  letter-spacing: 0.12em;
}

.inventory-hero h1 {
  margin: 0;
  font-size: clamp(42px, 7vw, 78px);
  line-height: 0.92;
  letter-spacing: -0.07em;
}

.hero-copy {
  max-width: 690px;
  margin: 16px 0 0;
  color: #cfc2bd;
  font-size: 16px;
  line-height: 1.5;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(170px, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.summary-card {
  padding: 20px;
}

.summary-card span {
  display: block;
  color: #a99d98;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.summary-card strong {
  display: block;
  margin-top: 10px;
  font-size: 34px;
  line-height: 1;
  letter-spacing: -0.05em;
}

.inventory-toolbar {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 190px 160px 190px auto;
  gap: 12px;
  align-items: end;
  margin-top: 18px;
  padding: 18px;
}

.search-wrap {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #c8a66a;
  font-weight: 900;
  pointer-events: none;
}

.search-wrap input {
  padding-left: 38px;
}

.inventory-report-shell input,
.inventory-report-shell select {
  min-height: 52px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  outline: 0;
  background: rgba(6, 4, 6, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  font-size: 15px;
  font-family: inherit;
}

.inventory-report-shell input::placeholder {
  color: #766c68;
}

.inventory-report-shell input:focus,
.inventory-report-shell select:focus {
  border-color: rgba(200, 166, 106, 0.65);
  box-shadow: 0 0 0 3px rgba(200, 166, 106, 0.14);
}

.inventory-report-shell label {
  display: grid;
  gap: 8px;
}

.inventory-report-shell label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.inventory-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 18px;
}

.inventory-card {
  padding: 20px;
  cursor: pointer;
  transition:
    transform 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease;
}

.inventory-card:hover {
  transform: translateY(-3px);
  border-color: rgba(200, 166, 106, 0.35);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
}

.card-top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.card-top h2 {
  margin: 0;
  font-size: clamp(22px, 3vw, 34px);
  line-height: 0.98;
  letter-spacing: -0.055em;
}

.value-pill {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-radius: 999px;
  color: #f1c979;
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
  background: rgba(200, 166, 106, 0.16);
  border: 1px solid rgba(200, 166, 106, 0.28);
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.meta-row span {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  color: #cfc2bd;
  font-size: 12px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.065);
  border: 1px solid rgba(255, 255, 255, 0.075);
}

.inventory-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 11px;
  margin-top: 16px;
}

.inventory-metrics div,
.detail-grid div,
.detail-hero div {
  padding: 14px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.065);
}

.inventory-metrics span,
.detail-grid span,
.detail-hero span,
.detail-notes span {
  display: block;
  margin-bottom: 6px;
  color: #9b8f8b;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.inventory-metrics strong,
.detail-grid strong,
.detail-hero strong {
  color: #fff8f1;
  font-size: 16px;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-top: 16px;
}

.stock-badge {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  padding: 0 12px;
  border-radius: 999px;
  color: #9df0bd;
  font-size: 12px;
  font-weight: 900;
  background: rgba(47, 170, 118, 0.18);
  border: 1px solid rgba(118, 224, 170, 0.23);
}

.stock-badge.low {
  color: #ffb4a8;
  background: rgba(155, 45, 45, 0.28);
  border-color: rgba(255, 118, 118, 0.25);
}

.details-link {
  color: #f1c979;
  font-size: 13px;
  font-weight: 900;
}

.empty-state {
  margin-top: 18px;
  padding: 46px 30px;
  text-align: center;
}

.empty-state h2 {
  margin: 0;
  font-size: clamp(32px, 5vw, 54px);
  letter-spacing: -0.06em;
}

.empty-state p {
  color: #cfc2bd;
}

.primary-action,
.ghost-action {
  min-height: 50px;
  padding: 0 18px;
  border-radius: 999px;
  border: 0;
  color: #fff8f1;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
}

.primary-action {
  background: linear-gradient(135deg, #8f123f, #c24a2f);
  box-shadow: 0 14px 32px rgba(194, 74, 47, 0.3);
}

.ghost-action {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
}

.primary-action:hover,
.ghost-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24);
}

/* ---------- Modal ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.inventory-modal {
  width: min(780px, 94vw);
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.modal-topline {
  height: 5px;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 40px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  padding: 24px 28px 28px;
  overflow-y: auto;
}

.detail-hero {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.detail-hero strong {
  color: #f1c979;
  font-size: 28px;
  letter-spacing: -0.05em;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 14px;
}

.detail-notes {
  margin-top: 14px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.065);
}

.detail-notes p {
  margin: 0;
  color: #cfc2bd;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

/* ---------- Responsive ---------- */

@media (max-width: 1100px) {
  .inventory-toolbar,
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }

  .inventory-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .inventory-report-shell {
    padding: 16px;
  }

  .inventory-hero {
    flex-direction: column;
    align-items: stretch;
  }

  .inventory-toolbar,
  .summary-grid,
  .inventory-metrics,
  .detail-hero,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .card-top {
    flex-direction: column;
  }

  .modal-overlay {
    padding: 12px;
    align-items: stretch;
  }

  .inventory-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 24px;
  }

  .modal-header {
    flex-direction: column;
  }

  .primary-action,
  .ghost-action {
    width: 100%;
  }
}
</style>